import { Hidden } from '@material-ui/core';
import Tooltip from '@cpg-its/ads.common.tooltip';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LabelAndValue } from '../../../global/components/data-display/label-value';
import { GuidedProcessSupportBlock } from '../../../global/components/guided-process-support-block';
import { DividerSection } from '../../../global/components/layout/divider-section';
import { ContentSpacer } from '../../../global/components/layout/generic-content-spacer';
import { PageHeader } from '../../../global/components/layout/page-header';
import { SideBarContent } from '../../../global/components/layout/sidebar-content';
import { getPodsContent } from '../../../global/ducks/epi-api';
import { getCurrencyFormat, getYearsFormat } from '../../../global/helpers/format-helpers';
import { isValueEmpty } from '../../../global/helpers/generic-helper';
import { TcsmStates } from '../../../root/tcsm-model';
import { Db, MyCpgPodsSettings, MyCpgPods, PodContent } from '../mycpg-pod-models';
import { tcsmColors } from '../../../global/constants/theme/theme-constants/colors';
import { TcsmModal } from '../../../global/components/tcsm-modal';
import { labelSupplementStyles } from '../../../global/constants/theme/styles/components/label-supplement-styles';
import { WysiwygWithReactLink } from '../../../global/components/wysiwyg-with-react-link';
import { YesNoIndicators } from '../../../global/models/api-model';
import { LabelSettingProps } from '../../../global/models/epi-model';

interface Props {
	settings: MyCpgPodsSettings;
}
export function PensionPlanDetail(props: Props) {
	
	const labelSettings = useSelector<TcsmStates, LabelSettingProps>(a => a.epiProps?.labelSettingState?.epiContent);
	const { settings } = props;
	const pods = useSelector<TcsmStates, MyCpgPods>(state => state?.mycpgPodsState?.pods);
	const dbPlan = pods?.db_data;	
	const dispatch = useDispatch();
	const [content, setContent] = useState<any>({});
	const [open, setOpen] = useState(false);
	const [selectedPlanDetail, setSelectedItem] = useState('');
	const notEnrolled = dbPlan?.content === PodContent.NOT_ENROLLED;
	const contentTemp = {};
	const tooltipLabelClasses = labelSupplementStyles();
	const handleClose = () => {
		setOpen(false);
	};
	
	const onPlanDetailClick = (plan: string) => {
		setSelectedItem(plan);
		setOpen(true);
	};

	const onContentSuccess = (key, html) => {
		contentTemp[key] = html;
		setContent({ ...contentTemp });
	};


	useEffect(() => {
		if (dbPlan?.db_annual_statement_path)
			dispatch(getPodsContent(
				dbPlan?.db_annual_statement_path,
				(data) => onContentSuccess(dbPlan?.db_annual_statement_path, data?.contentHtml),
			));

		if (dbPlan?.db_estimate_link_path)
			dispatch(getPodsContent(
				dbPlan?.db_estimate_link_path,
				(data) => onContentSuccess(dbPlan?.db_estimate_link_path, data?.contentHtml),
			));

		if (dbPlan?.db_estimate_request_path)
			dispatch(getPodsContent(
				dbPlan?.db_estimate_request_path,
				(data) => onContentSuccess(dbPlan?.db_estimate_request_path, data?.contentHtml),
			));

		dbPlan?.data?.forEach(db => {
			if (db?.content_path) {
				dispatch(getPodsContent(
					db?.content_path,
					(data) => onContentSuccess(db?.content_path, data?.contentHtml),
				));
			}
		});
	}, []);

	function RenderBeneficiaryStatement(db: Db){
		// The flags name is 'show_prsb_beneficiary_message?' 
		// Validate the flag without '?'  in case is changed.
		if ((db?.show_prsb_beneficiary_message === YesNoIndicators.Yes) ||
			(db && db['show_prsb_beneficiary_message?'] === YesNoIndicators.Yes)){		
			return (
				<ContentSpacer>
					<WysiwygWithReactLink content={props.settings.activeLayDBOnlyMessage} />				
				</ContentSpacer>
			);
		}
	}


	const renderActive = (db: Db) => {
		return (
			<DividerSection sectionText={db?.plan_title}>
				{
					db?.years_of_credited_service &&
					<ContentSpacer>

						<p className='h3-styled'>{`${getYearsFormat(db?.years_of_credited_service)} ${labelSettings?.yearsText ? labelSettings?.yearsText : 'years' }`}</p>
						<div className={tooltipLabelClasses.labelLayout}>
							<p className='grey-caption'>{settings?.creditedServicePensionYearLabel}</p>
							<div className='supplement-wrapper'>
								<Tooltip iconColor={tcsmColors.blue} title={settings?.creditedServiceHelper} />
							</div>
						</div>
					</ContentSpacer>
				}
				{
					db?.medicare_credited_service &&
					<ContentSpacer>
						<p className='h3-styled'>{`${getYearsFormat(db?.medicare_credited_service)} ${labelSettings?.yearsText ? labelSettings?.yearsText : 'years' }`}</p>
						<div className={tooltipLabelClasses.labelLayout}>
							<p className='grey-caption'>{settings?.creditedServiceMedicareYearLabel}</p>
							<div className='supplement-wrapper'>
								<Tooltip iconColor={tcsmColors.blue} title={settings?.medicareServiceHelper} />
							</div>
						</div>
					</ContentSpacer>
				}
				{
					db?.highest_average_compensation &&
					<ContentSpacer>
						<p className='h3-styled'>{getCurrencyFormat(db?.highest_average_compensation)}</p>
						<div className={tooltipLabelClasses.labelLayout}>
							<p className='grey-caption'>{settings?.highestAvgCompensationLabel}</p>
							<div className='supplement-wrapper'>
								<Tooltip iconColor={tcsmColors.blue} title={settings?.hacHelper} />
							</div>
						</div>
					</ContentSpacer>
				}
				{
				  RenderBeneficiaryStatement(db)
				}	
				{
					db?.beneficiaries && db?.beneficiaries?.length > 0 &&
					<ContentSpacer>
						<p className='h3-styled'>{settings?.beneficiariesTitle}</p>
						{
							db?.beneficiaries?.map(b => <p>{b?.display_name} ({b?.rider}/{b?.designation_type})</p>)
						}
					</ContentSpacer>
				}															
				{
					db?.content_path &&
					<button className='link-button' type='button' onClick={() => onPlanDetailClick(db?.content_path)}>{settings?.planDetailButtonLabel}</button>
				}
			</DividerSection>
		);

	};

	const renderRetired = (db: Db) => {
		return (
			<DividerSection sectionText={db?.plan_title}>
				{
					db?.monthly_pension &&
					<ContentSpacer>
						<p className='h3-styled'>{getCurrencyFormat(db?.monthly_pension)}</p>
						<div className={tooltipLabelClasses.labelLayout}>
							<p className='grey-caption'>{settings?.monthlyPensionBenefitLabel}</p>
							<div className='supplement-wrapper'>
								<Tooltip iconColor={tcsmColors.blue} title={settings?.pensionBenefitHelper} />
							</div>
						</div>
					</ContentSpacer>
				}
				{!isValueEmpty(db?.benefits_effective_date) && <LabelAndValue label={settings?.benefitEffectiveDateLabel} value={db?.benefits_effective_date} />}
				{!isValueEmpty(db?.retirement_date) && <LabelAndValue label={settings?.retirementDateLabel} value={db?.retirement_date} />}
				{!isValueEmpty(db?.payment_start_date) && <LabelAndValue label={settings?.paymentEffectiveDateLabel} value={db?.payment_start_date} />}
				{!isValueEmpty(db?.medical_deduction_amount) && <LabelAndValue label={settings?.medicalDeductionLabel} tooltipMessage={settings?.medicalDeductionHelper} value={getCurrencyFormat(db?.medical_deduction_amount)} />}
				{!isValueEmpty(db?.catastrophic_deduction_amount) && <LabelAndValue label={settings?.coverageReductionLabel} tooltipMessage={settings?.coverageReductionHelper} value={getCurrencyFormat(db?.catastrophic_deduction_amount)} />}
				{!isValueEmpty(db?.tax_withheld) && <LabelAndValue label={settings?.totalTaxWithholdingLabel} tooltipMessage={settings?.totalTaxHelper} value={getCurrencyFormat(db?.tax_withheld)} />}
				{
					db?.content_path &&
					<button className='link-button' type='button' onClick={() => onPlanDetailClick(db?.content_path)}>{settings?.planDetailButtonLabel}</button>
				}
			</DividerSection>
		);
	};

	const renderDbPlan = () => {
		return dbPlan?.data?.map(a => {
			if (a.highest_average_compensation) {
				return renderActive(a);
			}

			return renderRetired(a);

		});

	};


	const renderDetailSection = () => {

		return (
			<>
				{
					renderDbPlan()
				}
				{
					dbPlan?.db_annual_statement === YesNoIndicators.Yes &&
					<DividerSection>
						<WysiwygWithReactLink content={content[dbPlan?.db_annual_statement_path]} />
					</DividerSection>
				}
				{
					dbPlan?.db_estimate_link === YesNoIndicators.Yes &&
					<DividerSection >
						<WysiwygWithReactLink content={content[dbPlan?.db_estimate_link_path]} />
					</DividerSection>
				}
				{
					dbPlan?.db_estimate_request === YesNoIndicators.Yes &&
					<DividerSection>
						<WysiwygWithReactLink content={content[dbPlan?.db_estimate_request_path]} />
					</DividerSection>
				}
			</>);

	};

	return (
		<>
			<PageHeader headerText={settings?.pensionPlanDetailTitle} />
			<Hidden mdUp>
				<SideBarContent>
					<GuidedProcessSupportBlock title={settings?.pensionGpsTitle} contents={settings?.pensionGps} />
				</SideBarContent>
			</Hidden>
			{
				notEnrolled ?
					<DividerSection >
						<WysiwygWithReactLink content={settings?.dbPlanNotEnrolledMessage} />
					</DividerSection> :
					<>
						<TcsmModal
							open={open}
							handleClose={handleClose}
						>
							<WysiwygWithReactLink content={content[selectedPlanDetail]} />
						</TcsmModal>
						
						{

							renderDetailSection()
						}
					</>

			}

		</>
	);

}

